import { Cable, ExternalLink } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { EntitySyncControls } from './EntitySyncControls'
import { CobaltLaunchButton } from './CobaltLaunchButton'
import { CobaltStatusPanel } from './CobaltStatusPanel'
import { CobaltProvidersPanel } from './CobaltProvidersPanel'
import { CredentialValidationPanel } from './CredentialValidationPanel'
import { TestPatientDemographics } from './TestPatientDemographics'
import type {
  CobaltConnectionStatus,
  CobaltSyncConfig,
  CobaltSyncConfigUpdate,
  CobaltSyncEntity,
  CobaltSyncEntityState,
  CredentialStatusResponse,
  CredentialValidationResponse,
} from './types'

interface ConnectedConnectionSheetProps {
  discoverCobaltProviders?: boolean
  onProviderDiscoveryStarted?: () => void
  credential?: CredentialStatusResponse
  validation?: CredentialValidationResponse
  isValidationLoading: boolean
  isValidationError: boolean
  cobaltStatus?: CobaltConnectionStatus
  isCobaltStatusError: boolean
  isCobaltStatusFetching: boolean
  cobaltStatusError?: Error | null
  canManageCobaltSyncs: boolean
  isCobaltStarting: boolean
  cobaltSyncConfigs?: CobaltSyncConfig[]
  cobaltSyncEntities?: CobaltSyncEntityState[]
  isCobaltSyncLoading: boolean
  isCobaltSyncError: boolean
  // Separate from the sync state above: the schedule controls come from a
  // different query, and its failure must not disable manual syncing.
  isCobaltConfigLoading: boolean
  isCobaltConfigError: boolean
  startingCobaltSyncEntity?: CobaltSyncEntity
  cancellingCobaltSyncEntity?: CobaltSyncEntity
  updatingCobaltSyncConfigEntity?: CobaltSyncEntity
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpenIntake: () => void
  onRetryValidation: () => void
  onRetryCobaltStatus: () => void
  onStartCobaltOnboarding: () => void
  onStartCobaltSync: (entity: CobaltSyncEntity) => void
  onCancelCobaltSync: (entity: CobaltSyncEntity) => void
  onUpdateCobaltSyncConfig: (
    entity: CobaltSyncEntity,
    changes: CobaltSyncConfigUpdate
  ) => Promise<boolean>
}

export function ConnectedConnectionSheet({
  discoverCobaltProviders,
  onProviderDiscoveryStarted,
  credential,
  validation,
  isValidationLoading,
  isValidationError,
  cobaltStatus,
  isCobaltStatusError,
  isCobaltStatusFetching,
  cobaltStatusError,
  canManageCobaltSyncs,
  isCobaltStarting,
  cobaltSyncConfigs,
  cobaltSyncEntities,
  isCobaltSyncLoading,
  isCobaltSyncError,
  isCobaltConfigLoading,
  isCobaltConfigError,
  startingCobaltSyncEntity,
  cancellingCobaltSyncEntity,
  updatingCobaltSyncConfigEntity,
  open,
  onOpenChange,
  onOpenIntake,
  onRetryValidation,
  onRetryCobaltStatus,
  onStartCobaltOnboarding,
  onStartCobaltSync,
  onCancelCobaltSync,
  onUpdateCobaltSyncConfig,
}: ConnectedConnectionSheetProps) {
  const canUpdate =
    credential?.status === 'configured' &&
    credential.can_submit_credentials &&
    credential.candidate_status === 'none'
  const hasCredentialConnection = credential?.status === 'configured'
  const cobaltConnected = cobaltStatus === 'connected'
  const canUpdateCobalt = cobaltConnected && canManageCobaltSyncs
  const handleUpdate = () => {
    onOpenChange(false)
    onOpenIntake()
  }

  const handleCobaltOnboarding = () => {
    onOpenChange(false)
    onStartCobaltOnboarding()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="connected-connection-sheet"
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-y-auto rounded-l-xl p-0 sm:max-w-lg"
        onOpenAutoFocus={(event) => event.preventDefault()}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10 border-b border-border">
          <div className="flex items-center gap-2">
            <Cable className="size-5" />
            <SheetTitle>eClinicalWorks</SheetTitle>
          </div>
          <SheetDescription>Connection details</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4 p-4">
          {hasCredentialConnection &&
            (isCobaltStatusError || !cobaltConnected) && (
              <CobaltStatusPanel
                status={cobaltStatus}
                error={cobaltStatusError}
                canManage={canManageCobaltSyncs}
                isStarting={isCobaltStarting}
                isRetrying={isCobaltStatusFetching}
                onRetry={onRetryCobaltStatus}
                onStartOnboarding={handleCobaltOnboarding}
              />
            )}

          <CredentialValidationPanel
            credential={credential}
            validation={validation}
            isLoading={isValidationLoading}
            isError={isValidationError}
            onRetry={onRetryValidation}
          />

          {hasCredentialConnection && credential.can_review_credentials && (
            <TestPatientDemographics
              validation={validation}
              title="Test patient demographics"
            />
          )}

          {open && cobaltConnected && canManageCobaltSyncs && (
            <CobaltProvidersPanel
              autoDiscover={discoverCobaltProviders}
              onDiscoveryStarted={onProviderDiscoveryStarted}
            />
          )}

          <EntitySyncControls
            cobaltConnected={cobaltConnected}
            canManageCobalt={canManageCobaltSyncs}
            cobaltConfigs={cobaltSyncConfigs}
            cobaltEntities={cobaltSyncEntities}
            isCobaltLoading={isCobaltSyncLoading}
            isCobaltError={isCobaltSyncError}
            isCobaltConfigLoading={isCobaltConfigLoading}
            isCobaltConfigError={isCobaltConfigError}
            startingCobaltEntity={startingCobaltSyncEntity}
            cancellingCobaltEntity={cancellingCobaltSyncEntity}
            updatingCobaltConfigEntity={updatingCobaltSyncConfigEntity}
            computerUseEnabled={credential?.can_review_credentials ?? false}
            onStartCobalt={onStartCobaltSync}
            onCancelCobalt={onCancelCobaltSync}
            onUpdateCobaltConfig={onUpdateCobaltSyncConfig}
          />

          {credential?.candidate_status === 'submitted' && (
            <p className="text-sm text-muted-foreground">
              A connection update is already awaiting validation.
            </p>
          )}
        </div>

        {(canUpdate || canUpdateCobalt) && (
          <SheetFooter className="border-t border-border bg-neutral-ui-bg">
            {canUpdateCobalt && (
              <CobaltLaunchButton
                isStarting={isCobaltStarting}
                onClick={handleCobaltOnboarding}>
                Update Cobalt credentials
              </CobaltLaunchButton>
            )}
            {canUpdate && (
              <Button type="button" onClick={handleUpdate}>
                Update test patient or credentials
                <ExternalLink aria-hidden="true" />
              </Button>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}
